import moment from "moment";

import type { JollyRancherChipColor } from "veneer/Chip";

import {
  DeviceLastSeen,
  LastConnectedTier,
} from "cortex/ui/src/fleet/view/device/model";
import type { IndicatorColor, IndicatorType } from "cortex/ui/src/ui/indicator";
import { DEFAULT_TIME_FORMAT, utcToLocalFormat } from "cortex/ui/src/util/time";

export interface LastConnectedStatusMeta {
  chipColor: JollyRancherChipColor;
  color: IndicatorColor;
  icon: IndicatorType;
  label: LastConnectedTier;
  sublabel: string;
  tooltipSubtitle: string | null;
  tooltipTitle: string;
}

function capMissedCheckins(count: number): string {
  return count > 1000 ? "1000+" : String(count);
}

function getMissedCheckins(lastSeen: string): number {
  const diffInMinutes = moment.utc().diff(moment.utc(lastSeen), "minutes");
  return Math.floor(diffInMinutes / 10);
}

export const formatDateTimeLocal = (date: string, format?: string): string => {
  if (!date) {
    return "N/A";
  }
  return utcToLocalFormat(date, format || DEFAULT_TIME_FORMAT);
};

export function formatRelativeTime(value: string): string {
  if (!value) {
    return "N/A";
  }

  const now = moment.utc();
  const seen = moment.utc(value);

  if (!seen.isValid()) {
    return "N/A";
  }

  if (seen.isAfter(now)) return "Just now";

  const minutes = now.diff(seen, "minutes");
  const hours = now.diff(seen, "hours");
  const days = now.diff(seen, "days");
  const weeks = now.diff(seen, "weeks");
  const months = now.diff(seen, "months");
  const years = now.diff(seen, "years");

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  if (weeks < 5) return `${weeks}w`;
  if (months < 12) return `${months}mo`;
  return `${years}y`;
}

export function getLastConnectedStatusMeta(
  lastSeen: DeviceLastSeen | null,
): LastConnectedStatusMeta {
  if (lastSeen?.neverConnected) {
    return {
      chipColor: "gray",
      color: "gray",
      icon: "circle",
      label: "Never Connected",
      sublabel: "N/A",
      tooltipSubtitle: null,
      tooltipTitle: "Device has never been online",
    };
  }

  if (!lastSeen?.value) {
    return {
      chipColor: "red",
      color: "red",
      icon: "triangle-down",
      label: "Unreachable",
      sublabel: "4w+",
      tooltipSubtitle: "Missed check-ins: 1000+",
      tooltipTitle: "Not seen in 4+ weeks",
    };
  }

  const parsedLastSeen = moment(lastSeen.value, moment.ISO_8601, true);
  if (!parsedLastSeen.isValid()) {
    return {
      chipColor: "red",
      color: "red",
      icon: "triangle-down",
      label: "Unreachable",
      sublabel: "N/A",
      tooltipSubtitle: "Missed check-ins: N/A",
      tooltipTitle: "Last seen time invalid",
    };
  }

  const lastSeenMoment = parsedLastSeen.utc();
  const diffInMinutes = moment.utc().diff(lastSeenMoment, "minutes");
  const missedCheckins = getMissedCheckins(lastSeen.value);
  const relative = formatRelativeTime(lastSeen.value);
  const local = formatDateTimeLocal(lastSeen.value);
  const missed = `Missed check-ins: ${capMissedCheckins(missedCheckins)}`;

  if (diffInMinutes < 10) {
    if (lastSeen.isHibernating) {
      return {
        chipColor: "green",
        color: "green",
        icon: "moon",
        label: "Connected",
        sublabel: relative,
        tooltipSubtitle: "Hibernating",
        tooltipTitle: local,
      };
    }

    return {
      chipColor: "green",
      color: "green",
      icon: "triangle-up",
      label: "Connected",
      sublabel: relative,
      tooltipSubtitle: "Active",
      tooltipTitle: local,
    };
  }

  if (diffInMinutes < 60) {
    return {
      chipColor: "yellow",
      color: "yellow",
      icon: "triangle-down",
      label: "Missed Check-in",
      sublabel: relative,
      tooltipSubtitle: missed,
      tooltipTitle: local,
    };
  }

  if (diffInMinutes < 60 * 24) {
    return {
      chipColor: "yellow",
      color: "darkYellow",
      icon: "triangle-down",
      label: "Offline",
      sublabel: relative,
      tooltipSubtitle: missed,
      tooltipTitle: local,
    };
  }

  if (diffInMinutes >= 60 * 24 * 28) {
    return {
      chipColor: "red",
      color: "red",
      icon: "triangle-down",
      label: "Unreachable",
      sublabel: "4w+",
      tooltipSubtitle: "Missed check-ins: 1000+",
      tooltipTitle: "Not seen in 4+ weeks",
    };
  }

  return {
    chipColor: "red",
    color: "red",
    icon: "triangle-down",
    label: "Unreachable",
    sublabel: relative,
    tooltipSubtitle: missed,
    tooltipTitle: local,
  };
}

export function getLastConnectedTier(
  lastSeen: DeviceLastSeen | null,
): LastConnectedTier {
  return getLastConnectedStatusMeta(lastSeen).label;
}
